import { ResponsiveContainer } from '@/components/layout/responsive-container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { convexQuery, useConvexMutation } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronLeft, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';

export const TemplateView = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: Id<'templates'> }>();

  // Early validation: ensure id param exists
  if (!id) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div className="text-sm text-red-600">
          {t('templates:invalidTemplateId')}
        </div>
      </div>
    );
  }

  return <TemplateViewInner id={id} />;
};

const TemplateViewInner = ({ id }: { id: Id<'templates'> }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: template, isLoading } = useQuery(
    convexQuery(api.templates.get, { id }),
  );

  const { mutate: duplicateTemplate, isPending: isDuplicating } = useMutation({
    mutationFn: useConvexMutation(api.templates.duplicate),
  });

  const handleDuplicate = () => {
    duplicateTemplate(
      { id },
      {
        onSuccess: () => {
          toast.success(t('templates:templateDuplicated'));
        },
        onError: (error) => {
          toast.error(t('errors:errorDuplicatingTemplate'));
          console.error('Error duplicating template:', error);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div className="text-sm text-gray-500">{t('templates:loading')}</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div className="text-sm text-gray-500">
          {t('templates:templateNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full w-full flex-col">
      <ResponsiveContainer
        scrollAwareBorder
        className="flex w-full flex-row items-center gap-3 bg-white py-4 md:pt-6">
        <Button
          className="cursor-pointer md:hidden"
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}>
          <ChevronLeft className="size-5" />
        </Button>
        <h1 className="text-2xl font-bold">{template.name}</h1>
        <Button
          variant="outline"
          size="sm"
          disabled={isDuplicating}
          loading={isDuplicating}
          onClick={handleDuplicate}>
          <Copy className="size-4" />
          {t('templates:duplicate')}
        </Button>
      </ResponsiveContainer>
      <ResponsiveContainer className="flex w-full flex-1 flex-col gap-2 space-y-4 overflow-y-auto bg-white md:pb-6">
        {template.sections.map((section, index) => (
          <div key={index} className="rounded-lg border bg-gray-100 p-4">
            <div className="flex flex-col gap-2">
              <div className="flex-1 space-y-3">
                {/* Section title */}
                <div className="grid gap-2">
                  <span className="text-xs font-medium text-gray-600">
                    {t('templates:sectionInstructions')}
                  </span>
                  <Input
                    value={section.title}
                    className="bg-white shadow-none"
                    readOnly
                  />
                </div>

                {/* Section Prompt/Instructions */}
                <div className="grid gap-2">
                  <span className="text-xs font-medium text-gray-600">
                    {t('templates:sectionInstructions')}
                  </span>
                  <Textarea
                    value={section.prompt}
                    className="bg-white shadow-none"
                    rows={4}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </ResponsiveContainer>
    </div>
  );
};
